import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import Badge from "@/components/ui/Badge";
import Button from "@/components/ui/Button";
import { needsYouRowCopy } from "@/lib/needsYouCopy";
import { cn } from "@/lib/utils";
import { revealOnPage, splitHref } from "@/lib/revealOnPage";
import type { NeedsYouItem } from "@/types/needsYou";

/**
 * The Needs-you rows (in-app design spec § 12 § 3.4 item 2, Rule 1): an
 * ordered list — blocking first, then decisions oldest first, then setup, the
 * order the server ranked — each row its kind word, what needs the user, why,
 * and ONE action named with the row's subject ("Review result: Subtitle names
 * the activity"). The Dashboard, Active and the bell render this component
 * over the same query, so the list is one list.
 */
export default function NeedsYouList({
  items,
  labelledBy,
  compact = false,
  onOpen,
}: {
  items: ReadonlyArray<NeedsYouItem>;
  labelledBy?: string;
  /** The bell's dense rendering (a 400 px panel): no detail line, and the
   *  row STACKS -- badge + title on the first line, the action below,
   *  right-aligned -- so the title keeps the whole text column instead of
   *  the ~26-127 px a beside-the-button layout left it (ENG-APP-UX-TUNEUP
   *  C9 W11, pass-3 M3-R27-1). The Dashboard and Active rows keep the
   *  beside layout byte-for-byte. */
  compact?: boolean;
  /** Called when a row's action is taken (the bell closes itself). */
  onOpen?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // A same-page target (`/#competitors`) is revealed in place; any other
  // href navigates.
  const open = (href: string) => {
    onOpen?.();
    const [path, hash] = splitHref(href);
    if (hash && path === location.pathname && revealOnPage(hash)) return;
    navigate(href);
  };

  return (
    <ol aria-labelledby={labelledBy} className="space-y-2">
      {items.map((item) => {
        const copy = needsYouRowCopy(t, item, i18n.language);
        return (
          <li
            key={item.id}
            data-testid={`needs-you-row-${item.type}`}
            className={cn(
              "border-divider bg-surface rounded-xl border p-3",
              compact
                ? "grid grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-2"
                : "flex flex-wrap items-start gap-3",
            )}
          >
            <Badge variant={copy.tone}>{copy.tag}</Badge>
            <div className="min-w-0 flex-1">
              <p
                className={cn(
                  "text-ink text-sm font-medium",
                  compact ? "line-clamp-3" : "line-clamp-2",
                )}
              >
                {copy.title}
              </p>
              {!compact && (
                <p className="text-ink-soft mt-0.5 text-sm">{copy.detail}</p>
              )}
            </div>
            <Button
              size="compact"
              variant={item.kind === "blocking" ? "primary" : "secondary"}
              className={
                compact ? "col-span-2 max-w-full justify-self-end" : undefined
              }
              onClick={() => open(item.href)}
              // The action names its subject; when the visible label already
              // does ("Reconnect App Store Connect"), it is the whole name.
              aria-label={
                copy.actionLabel.includes(copy.subject)
                  ? copy.actionLabel
                  : t("dashboard.needsYou.actionAriaLabel", {
                      action: copy.actionLabel,
                      subject: copy.subject,
                    })
              }
            >
              {copy.actionLabel}
              <ChevronRight size={12} aria-hidden="true" />
            </Button>
          </li>
        );
      })}
    </ol>
  );
}
